"use client";

import {useState} from "react";
import {useNavigate} from "react-router-dom";
import {Home, Monitor, User, ChevronLeft, Calendar, BarChart3} from "lucide-react";
import {Drawer, DrawerClose, DrawerContent, DrawerHeader, DrawerTitle} from "../../components/ui/drawer";
import {Button} from "../../components/ui/button";
import {useSidebar} from "../../components/ui/sidebar";
import {cn} from "../../lib/utils";

// Importing game icons
import SudokuIcon from "../../components/Assets/sudoku.png";
import CombinationIcon from "../../components/Assets/combination.png";
import NonoIcon from "../../components/Assets/nonogram.png";
import SolitareIcon from "../../components/Assets/solitare.png";
import SudoblockIcon from "../../components/Assets/sudoblock.png";
import SudotetroidIcon from "../../components/Assets/sudotetroid.png";
import WordleIcon from "../../components/Assets/wordle_logo.png";
import WordSearchIcon from "../../components/Assets/word_search_logo.png";
import MinequestIcon from "../../components/Assets/minequest_logo.png";
import SpellingBeeIcon from "../../components/Assets/spelling_bee_logo.png";
import ConnectionsIcon from "../../components/Assets/connections_logo.png";

// Mapping games to icons
const pageIcons = {
    Sudoku: SudokuIcon,
    Combinations: CombinationIcon,
    Nonogram: NonoIcon,
    Solitaire: SolitareIcon,
    Sudoblock: SudoblockIcon,
    Sudotetroid: SudotetroidIcon,
    Wordle: WordleIcon,
    Connections: ConnectionsIcon,
    Minequest: MinequestIcon,
    "Spelling Bee": SpellingBeeIcon,
    "Word Search": WordSearchIcon,
};

const BottomNavBar = () => {
    const [activeTab, setActiveTab] = useState(0);
    const navigate = useNavigate();
    const {structuredPages, brand} = useSidebar(); // Extract structuredPages from context
    const [open, setOpen] = useState(false); // State for drawer open/close
    const [selectedGame, setSelectedGame] = useState(null); // Store selected game

    const handleNavigation = (index) => {
        setActiveTab(index);
        switch (index) {
            case 0:
                setOpen(false);
                navigate("/");
                break;
            case 1:
                setOpen(true); // Open the game drawer
                setSelectedGame(null); // Reset selected game
                break;
            case 2:
                setOpen(false);
                if (brand === "Denda" || brand === "testing") {
                    navigate("/Administration");
                }
                break;
            case 3:
                setOpen(false);
                navigate("/Settings");
                break;
            case 4:
                setOpen(false);
                navigate("/Profile");
                break;
            default:
                break;
        }
    };

    const navigateToGame = (path) => {
        navigate(path);
        setOpen(false);
    };

    return (
        <>
            {/* Bottom Navigation Bar */}
      {!open && (
          <div
              className="fixed bottom-0 left-0 !z-[999] right-0 border-t border-purple-700/100 shadow-lg bg-[var(--header-color-dark)] rounded-t-2xl">
              <div className="flex items-center justify-around h-16">
                  <NavButton icon={<Home className="h-5 w-5 dark:stroke-white"/>} label="Home"
                             isActive={activeTab === 0} onClick={() => handleNavigation(0)}/>
                  <NavButton icon={<GameIcon className="h-5 w-5 dark:stroke-white"/>} label="Games"
                             isActive={activeTab === 1} onClick={() => handleNavigation(1)}/>
                  {
                      brand === "Denda" || brand === "testing" ? (
                          <NavButton icon={<Monitor className="h-5 w-5 dark:stroke-white"/>} label="Admin"
                                     isActive={activeTab === 2} onClick={() => handleNavigation(2)}/>
                      ) : ("")
                  }
                  <NavButton icon={<SettingsIcon className="h-5 w-5 dark:stroke-white"/>} label="Settings"
                             isActive={activeTab === 3} onClick={() => handleNavigation(3)}/>
                  <NavButton icon={<User className="h-5 w-5 dark:stroke-white"/>} label="Profile"
                             isActive={activeTab === 4} onClick={() => handleNavigation(4)}/>
              </div>
          </div>
      )}

      {/* Drawer for Games & Game Details */}
            <Drawer open={open} onOpenChange={setOpen}>
                <DrawerContent className="fixed inset-x-0 bottom-0 rounded-t-xl  bg-[var(--header-color-dark)]">
                    <div
                        className="fixed bottom-0 left-0 !z-[999] right-0 border-t border-purple-700/100 shadow-lg bg-[var(--header-color-dark)] rounded-t-2xl">
                        <div className="flex items-center justify-around h-16">
                            <NavButton icon={<Home className="h-5 w-5 dark:stroke-white"/>} label="Home"
                                       isActive={activeTab === 0} onClick={() => handleNavigation(0)}/>
                            <NavButton icon={<GameIcon className="h-5 w-5 dark:stroke-white"/>} label="Games"
                                       isActive={activeTab === 1} onClick={() => handleNavigation(1)}/>
                            {
                                brand === "Denda" || brand === "testing" ? (
                                    <NavButton icon={<Monitor className="h-5 w-5 dark:stroke-white"/>} label="Admin"
                                               isActive={activeTab === 2} onClick={() => handleNavigation(2)}/>
                                ) : ("")
                            }
                            <NavButton icon={<SettingsIcon className="h-5 w-5 dark:stroke-white"/>} label="Settings"
                                       isActive={activeTab === 3} onClick={() => handleNavigation(3)}/>
                            <NavButton icon={<User className="h-5 w-5 dark:stroke-white"/>} label="Profile"
                                       isActive={activeTab === 3} onClick={() => handleNavigation(4)}/>
                        </div>
                    </div>
                    <DrawerHeader className="border-b border-border">
                        <DrawerTitle className="flex items-center text-xl font-semibold">
                            {selectedGame && (
                                <Button variant="ghost" size="icon" onClick={() => setSelectedGame(null)}
                                        className="mr-2">
                                    <ChevronLeft className="h-5 w-5"/>
                                </Button>
                            )}
                            {selectedGame ? selectedGame : "Games Library"}
                        </DrawerTitle>
                    </DrawerHeader>

                    {/* If no game is selected, show game list */}
                    {!selectedGame ? (
                        <div className="p-4 max-h-[70vh] overflow-y-auto">
                            <div className="grid grid-cols-2 gap-3">
                                {Object.keys(structuredPages)
                                    .filter((gameName) => gameName.toLowerCase() !== "administration" && gameName !== "10 Daagse HLN" && gameName !== "10 Daagse")
                                    .map((gameName) => (
                                        <GameCard key={gameName} name={gameName} icon={pageIcons[gameName]}
                                                  onClick={() => setSelectedGame(gameName)}/>
                                    ))}
                            </div>
                        </div>
                    ) : (
                        // If a game is selected, show Planning & Statistics options
                        <div className="p-6 space-y-4">
                            <Button variant="outline" size="lg" className="w-full justify-start text-lg font-medium"
                                    onClick={() => navigateToGame(`${selectedGame}`)}>
                                <Calendar className="mr-3 h-5 w-5"/>
                                Planning
                            </Button>
                            <Button variant="outline" size="lg" className="w-full justify-start text-lg font-medium"
                                    onClick={() => navigateToGame(`${selectedGame}/Statistics`)}>
                                <BarChart3 className="mr-3 h-5 w-5"/>
                                Statistics
                            </Button>
                            <DrawerClose asChild>
                                <Button variant="ghost" className="w-full mt-2">
                                    Close
                                </Button>
                            </DrawerClose>
                        </div>
                    )}
                </DrawerContent>
            </Drawer>
        </>
    );
};

// Navigation Button Component
const NavButton = ({icon, label, isActive, onClick}) => {
    return (
        <button onClick={onClick}
                className={cn("flex flex-col items-center justify-center w-full h-full transition-colors", isActive ? "text-purple-400" : "text-muted-foreground hover:text-foreground")}>
            {icon}
            <span className="text-xs mt-1 ">{label}</span>
        </button>
    );
};

// Game Icon Component
const GameIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" {...props}>
        <path d="M10 2v2.5A1.5 1.5 0 0 1 8.5 6v0A1.5 1.5 0 0 1 7 4.5V2"/>
        <path d="M17 2v2.5A1.5 1.5 0 0 1 15.5 6v0A1.5 1.5 0 0 1 14 4.5V2"/>
        <path d="M22 19a9 9 0 0 1-9 9c-4.97 0-9-4.03-9-9s4.03-9 9-9h9Z"/>
        <path d="M14.5 16.5h-5"/>
        <path d="M12 14v5"/>
    </svg>
);

// Game Card Component
const GameCard = ({name, icon, onClick}) => {
    return (
        <button onClick={onClick}
                className="flex flex-col items-center p-4 bg-card hover:bg-accent rounded-xl border border-border shadow-sm transition-all hover:shadow-md">
            <div className="w-12 h-12 mb-2 flex items-center justify-center">
                {icon ? <img src={icon || "/placeholder.svg"} alt={name}
                             className="max-w-full max-h-full object-contain"/> :
                    <GameIcon className="h-10 w-10 text-primary"/>}
            </div>
            <span className="text-sm font-medium text-center">{name}</span>
        </button>
    );
};

const SettingsIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" {...props}>
        <circle cx="12" cy="12" r="3"/>
        <path d="M19 12a7 7 0 0 1-7 7"/>
        <path d="M5 12a7 7 0 0 1 7-7"/>
    </svg>
);

export default BottomNavBar;
